import { useRef } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import CancelIcon from '@mui/icons-material/Cancel'
import { getMerchantPhotoUrl } from '../../utils/merchantPhotoUtils'

/**
 * Gallery photo card with drag-to-reorder support
 * Layout: [drag handle] [preview] [name] [delete icon]
 * Supports both mouse drag (desktop) and touch drag (mobile)
 */
export default function GalleryPhotoCard({
	photo,
	index,
	onDelete,
	onReplace,
	onDragStart,
	onDragOver,
	onDragEnd,
	onDragLeave,
	onTouchStart,
	onTouchMove,
	onTouchEnd,
	isDragging,
	dropPosition, // 'before' | 'after' | null
	isMobile,
	hideDragHandle = false
}) {
	const url = getMerchantPhotoUrl(photo)
	const orderNumber = photo.order || (index + 1)
	const cardRef = useRef(null)
	const replaceInputRef = useRef(null)

	// Touch handlers for mobile
	const handleTouchStart = (e) => {
		if (onTouchStart) {
			onTouchStart(e, index, cardRef.current)
		}
	}

	const handleTouchMove = (e) => {
		if (onTouchMove) {
			onTouchMove(e, index)
		}
	}

	const handleTouchEnd = (e) => {
		if (onTouchEnd) {
			onTouchEnd(e)
		}
	}

	return (
		<Box
			sx={{
				position: 'relative',
				// Drop indicator line before (top on mobile, left on desktop)
				'&::before': dropPosition === 'before' ? {
					content: '""',
					position: 'absolute',
					...(isMobile ? {
						top: '-6px',
						left: 0,
						right: 0,
						height: '3px',
						width: 'auto'
					} : {
						top: 0,
						bottom: 0,
						left: '-10px',
						width: '3px',
						height: 'auto'
					}),
					bgcolor: '#9F7AEA',
					borderRadius: '2px',
					zIndex: 10
				} : {},
				// Drop indicator line after (bottom on mobile, right on desktop)
				'&::after': dropPosition === 'after' ? {
					content: '""',
					position: 'absolute',
					...(isMobile ? {
						bottom: '-6px',
						left: 0,
						right: 0,
						height: '3px',
						width: 'auto'
					} : {
						top: 0,
						bottom: 0,
						right: '-10px',
						width: '3px',
						height: 'auto'
					}),
					bgcolor: '#9F7AEA',
					borderRadius: '2px',
					zIndex: 10
				} : {}
			}}
		>
			<Box
				ref={cardRef}
				draggable={!isMobile && !hideDragHandle}
				onDragStart={(e) => !isMobile && !hideDragHandle && onDragStart?.(e, index)}
				onDragOver={(e) => !isMobile && !hideDragHandle && onDragOver?.(e, index)}
				onDragLeave={!isMobile && !hideDragHandle ? onDragLeave : undefined}
				onDragEnd={!isMobile && !hideDragHandle ? onDragEnd : undefined}
				onTouchStart={isMobile && !hideDragHandle ? handleTouchStart : undefined}
				onTouchMove={isMobile && !hideDragHandle ? handleTouchMove : undefined}
				onTouchEnd={isMobile && !hideDragHandle ? handleTouchEnd : undefined}
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '12px',
					padding: '10px 16px',
					bgcolor: '#FFFFFF',
					border: isDragging ? '1px dashed #9F7AEA' : '1px solid #CBD5E0',
					borderRadius: '12px',
					cursor: hideDragHandle ? 'default' : (isMobile ? 'default' : 'grab'),
					opacity: isDragging ? 0.5 : 1,
					transition: 'all 0.2s ease',
					userSelect: 'none',
					touchAction: hideDragHandle ? 'auto' : 'none',
					'&:active': { cursor: hideDragHandle ? 'default' : (isMobile ? 'default' : 'grabbing') },
					'&:hover': !isMobile && !hideDragHandle ? {
						borderColor: '#9F7AEA',
						boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
					} : {}
				}}
			>
				{/* Drag handle - hidden when hideDragHandle is true */}
				{!hideDragHandle && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							alignSelf: 'stretch',
							cursor: 'grab',
							'&:active': { cursor: 'grabbing' }
						}}
					>
						<DragIndicatorIcon
							sx={{
								color: 'rgba(0, 0, 0, 0.6)',
								fontSize: '20px'
							}}
						/>
					</Box>
				)}

				{/* Preview image and name container */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '10px',
						flex: 1
					}}
				>
					{/* Preview image - click to replace */}
					<Box
						onClick={(e) => {
							e.stopPropagation()
							if (onReplace) replaceInputRef.current?.click()
						}}
						sx={{
							width: '61px',
							height: '61px',
							borderRadius: '6px',
							overflow: 'hidden',
							bgcolor: '#F7F6F3',
							flexShrink: 0,
							cursor: onReplace ? 'pointer' : 'default',
							position: 'relative',
							'&:hover .replace-overlay': onReplace ? {
								opacity: 1
							} : {}
						}}
					>
						<img
							src={url}
							alt={`Photo ${orderNumber}`}
							draggable={false}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								pointerEvents: 'none'
							}}
						/>
						{onReplace && (
							<Box
								className="replace-overlay"
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									bgcolor: 'rgba(0,0,0,0.4)',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									opacity: 0,
									transition: 'opacity 0.2s ease',
									borderRadius: '6px'
								}}
							>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '10px', color: '#fff' }}>
									Replace
								</Typography>
							</Box>
						)}
						{onReplace && (
							<input
								ref={replaceInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png,image/webp"
								onChange={(e) => {
									const file = e.target.files?.[0]
									if (file) {
										onReplace(photo.id, file)
										e.target.value = ''
									}
								}}
								style={{ display: 'none' }}
							/>
						)}
					</Box>

					{/* Photo name */}
					<Typography
						sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: '16px',
							lineHeight: '1.5em',
							color: '#2D3748'
						}}
					>
						Photo {orderNumber}
					</Typography>
				</Box>

				{/* Delete button */}
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation()
						onDelete()
					}}
					onTouchStart={(e) => {
						e.stopPropagation()
					}}
					onTouchEnd={(e) => {
						e.stopPropagation()
						e.preventDefault()
						onDelete()
					}}
					sx={{
						p: 0,
						touchAction: 'manipulation',
						'&:hover': {
							bgcolor: 'transparent'
						}
					}}
				>
					<CancelIcon
						sx={{
							fontSize: '35px',
							color: 'rgba(0, 0, 0, 0.08)',
							'&:hover': {
								color: 'rgba(229, 62, 62, 0.6)'
							}
						}}
					/>
				</IconButton>
			</Box>
		</Box>
	)
}
